<template>
    <div
    class="navigation-breadcrumbs-wrapper"
    :class="{
        'mobile': getMobileAppState,
        'light-background': lightBackground,
        'non-authenticated-feature': nonAuthenticatedFeature,
        'no-parent-entries': !menuParentEntries || !menuParentEntries.length,
        'dark-mode': getMobileAppState && getAppThemeMode === 'dark'
    }">
        <!-- Parent Entries -->
        <div
        v-if="menuParentEntries?.length && !getMobileAppState && !nonAuthenticatedFeature"
        class="parent-entries">
            <div v-for="(entry, index) in menuParentEntries" :key="`breadcrumbEntry${entry.id}`" class="parent-entry-wrapper">
                <div v-if="(!entry.link && !entry.internalClick) || menuParentEntries.length === 1" class="text-parent">{{entry.text}}</div>
                <button-link v-else internalClick :label="entry.text" @clicked="handleBreadcrumbClick(entry)"/>
                <!-- Parent Breadcrumbs separator -->
                <icon-arrow-right v-if="index < (menuParentEntries.length - 1)" class="separator"/>
            </div>
        </div>
        <div
        v-if="menuParentEntries?.length && (getMobileAppState || nonAuthenticatedFeature)"
        class="parent-entry"
        :class="{'dark-mode': getAppThemeMode === 'dark'}">
            {{menuParentEntries[menuParentEntries.length - 1].text}}
        </div>
        <!-- Current Area -->
        <div class="current-area" :class="{'double-line': !menuParentEntries || !menuParentEntries.length, 'dark-mode': getAppThemeMode === 'dark'}">{{currentArea}}</div>
    </div>
</template>

<script setup>
import ButtonLink from '@/components/generic/buttons/Link'
import IconArrowRight from '@/assets/icons/arrow-right.svg?inline'
/* Vue */
import { navigateByPath } from '@/router/helpers/navigation'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
/* Assets & Helpers */
import { breadcrumsGenerator } from '@/assets/scripts/parsers/menu'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

const { getMobileAppState, getAppThemeMode } = mapGetters()
const { getMenu } = mapGetters(moduleMapper.MENU)

/* Emmitters */
const emit = defineEmits(['parentBreadcrumbClicked'])

/* Props
@nonAuthenticatedFeature {Boolean} prop to apply specific styles for this type of features
@customBreadcrumbs {Object} with:
	currentArea {String}
	parentMenus {Array} in which each object has:
        - id
        - text {String} text to display
        - link {String} (opcional) link to redirect user if clicked
@lightBackground {Boolean} - prop to apply styles to make this content visible in light backgrouns
*/
const props = defineProps({
    nonAuthenticatedFeature: Boolean,
	customBreadcrumbs: Object,
    lightBackground: Boolean
})

const currentRoute = useRoute()

const currentArea = computed(() => {
    const currentAreaInfo = props.customBreadcrumbs || breadcrumsGenerator(getMenu.value, currentRoute.path)
	return currentAreaInfo.currentArea
})

const menuParentEntries = computed(() => {
    const currentAreaInfo = props.customBreadcrumbs?.parentMenus ? props.customBreadcrumbs : breadcrumsGenerator(getMenu.value, currentRoute.path)
	return currentAreaInfo.parentMenus
})

const handleBreadcrumbClick = entry => {
    if (getMobileAppState.value) {
        /* TODO add mobile logic */
    } else {
       entry.internalClick ? emit('parentBreadcrumbClicked') : navigateByPath(entry.link)
    }
}
</script>

<style lang="scss" scoped>
.navigation-breadcrumbs-wrapper {
    width: auto;
    height: auto;

    display: flex;
    flex-direction: column;
    overflow: hidden;

    &:not(.mobile) {
        padding-bottom: 16px;
    }

    &.no-parent-entries {
        justify-content: center;
    }

    .parent-entries {
        width: auto;
        height: 16px;
        margin-bottom: 8px;

        display: flex;
        align-items: center;
        gap: 8px;

        .parent-entry-wrapper {
            width: auto;

            display: flex;
            align-items: center;
            gap: 8px;

            .text-parent {
                @include H7-Regular;
                color: var(--secondary-dark-1);
            }

            svg {
                width: 16px;
                height: 16px;

                :deep(.change-color-fill) {
                    fill: var(--secondary-dark-1);
                }
            }
        }
    }

    .current-area {
        width: auto;
        height: auto;
        min-height: 48px;

        @include H1-SemiBold;
        color: var(--secondary-dark-3);
        line-height: 48px;

        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    &.mobile {
        width: 100%;
        height: 40px;
        padding: 0 62px;

        justify-content: center;
        text-align: center;

        .parent-entry {
            width: 100%;
            height: 13px;

            @include H8-Regular;
            line-height: 13px;
            color: var(--secondary-light-2);
        }

        .current-area {
            width: 100%;
            height: 20px;
            min-height: 20px;

            @include H5-Medium;
            line-height: 20px;
            color: var(--secondary-light-4);
        }

        &.light-background, &.dark-mode {
            .parent-entry {
                color: var(--secondary-dark-1);
            }

            .current-area {
                color: var(--secondary-dark-3);
            }
        }
    }

    &.non-authenticated-feature {
        width: 100%;
        height: 40px;
        padding: 0 40px;

        justify-content: center;
        text-align: center;

        .parent-entry {
            margin-bottom: 2px;
            @include H6-Regular;
            color: var(--secondary-light-4);
            line-height: 18px;

            &.dark-mode {
                color: var(--secondary-dark-3);
            }
        }

        .current-area {
            min-height: 22px;
            @include H4-Medium;
            color: var(--secondary-light-4);
            line-height: 20px;
            &.dark-mode {
                color: var(--secondary-dark-3);
            }
        }

        &.mobile {
            padding: 0 68px;

            .current-area {
                @include H5-Medium;
                line-height: 20px;

                &.double-line {
                    height: auto;

                    white-space: unset;
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 2; /* number of lines to show */
                }
            }
        }
    }
}
</style>
